/*
 * Data hooks for pages. Every read is a query (cached, deduplicated), every write a mutation that
 * invalidates what it changes. Pages import from '@/services/queries' and never call `api` directly.
 */
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { api, onSessionExpired } from '../index';
import type {
  CancelRequest,
  ContactMessage,
  ExtensionRequest,
  Review,
  TripRatingInput,
  Currency,
  EligibilityRequestInput,
  ExperienceBookingInput,
  ExperienceFilters,
  ExperienceSelection,
  PartnerLead,
  ProfileUpdate,
  VehicleBookingInput,
  VehicleFilters,
  VehicleKind,
  VehicleSearch,
} from '../domain';
import { qk } from './keys';
import { useChatLiveStatus } from '../realtime/useChatLive';

export { qk };

/* ── Config and reference data ── */
export const usePlatformConfig = () => useQuery({ queryKey: qk.config, queryFn: api.config.get, staleTime: Infinity });
export const useVehicleCities = (kind: VehicleKind) =>
  useQuery({ queryKey: qk.cities(kind), queryFn: () => api.cities.vehicles(kind), staleTime: Infinity });
export const useExperienceCities = () =>
  useQuery({ queryKey: qk.experienceCities, queryFn: api.cities.experiences, staleTime: Infinity });
export const useDestinations = () =>
  useQuery({ queryKey: qk.destinations, queryFn: api.cities.destinations, staleTime: Infinity });

/* ── Session ── */
export const useSession = () => useQuery({ queryKey: qk.session, queryFn: api.auth.session, staleTime: Infinity });

function useAfterAuthChange() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.session });
    void qc.invalidateQueries({ queryKey: qk.favourites });
    void qc.invalidateQueries({ queryKey: qk.trips });
    void qc.invalidateQueries({ queryKey: qk.carts });
    void qc.invalidateQueries({ queryKey: qk.threads });
  };
}

export function useLogin() {
  const done = useAfterAuthChange();
  return useMutation({
    mutationFn: (v: { email: string; password: string }) => api.auth.login(v.email, v.password),
    onSuccess: done,
  });
}
export function useRegister() {
  const done = useAfterAuthChange();
  return useMutation({ mutationFn: api.auth.register, onSuccess: done });
}
/** Google sign-in (P7): the mutation takes the Google Identity Services credential (ID token). */
export function useGoogleSignIn() {
  const done = useAfterAuthChange();
  return useMutation({ mutationFn: (credential: string) => api.auth.google(credential), onSuccess: done });
}
/** Signed out (logout or expired session): the interface shows a guest at once, the account's data is dropped. */
function dropAccount(qc: QueryClient) {
  qc.setQueryData(qk.session, null);
  qc.removeQueries({ queryKey: qk.trips });
  qc.removeQueries({ queryKey: qk.carts });
  qc.removeQueries({ queryKey: qk.threads });
  qc.removeQueries({ queryKey: qk.reviews });
  qc.removeQueries({ queryKey: qk.stats });
  qc.removeQueries({ queryKey: qk.partnerStatus });
  void qc.invalidateQueries({ queryKey: qk.favourites });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.auth.logout,
    onSuccess: () => dropAccount(qc),
  });
}

/**
 * CK-02 / AU-02: the http layer ended an expired session (an expired token before an authed call, or a 401 to a
 * request that carried it). The session query is reset and the account's data dropped (as on logout), then
 * `onExpired` runs: the shell sends the user to sign in. Mounted once, by the shell.
 */
export function useSessionExpired(onExpired: () => void) {
  const qc = useQueryClient();
  const latest = useRef(onExpired);
  useEffect(() => {
    latest.current = onExpired;
  });
  useEffect(
    () =>
      onSessionExpired(() => {
        dropAccount(qc);
        latest.current();
      }),
    [qc],
  );
}
export const useForgotPassword = () => useMutation({ mutationFn: (email: string) => api.auth.forgotPassword(email) });
export const useResetPassword = () =>
  useMutation({ mutationFn: (v: { code: string; password: string }) => api.auth.resetPassword(v.code, v.password) });

/* ── Vehicles ── */
export const useVehicleSearchResults = (search: VehicleSearch, filters: VehicleFilters) =>
  useQuery({
    queryKey: qk.vehicleSearch(search, filters),
    queryFn: () => api.vehicles.search(search, filters),
    placeholderData: keepPreviousData,
  });
export const useVehicleCategories = (kind: VehicleKind, city: string) =>
  useQuery({ queryKey: qk.categories(kind, city), queryFn: () => api.vehicles.categories(kind, city) });
export const useFeaturedVehicles = (city: string) =>
  useQuery({ queryKey: qk.featured(city), queryFn: () => api.vehicles.featured(city) });
export const useVehicle = (id: string) => useQuery({ queryKey: qk.vehicle(id), queryFn: () => api.vehicles.get(id) });
export const useSimilarVehicles = (id: string) =>
  useQuery({ queryKey: qk.similar(id), queryFn: () => api.vehicles.similar(id) });
/** The price of record. `enabled` is false until dates and times are valid. */
/** `currency`: the chosen one, sent to the server quote for the online charge in it (PR-11, `Quote.converted`). */
export const useVehicleQuote = (id: string, search: VehicleSearch, enabled = true, currency: Currency = 'MAD') =>
  useQuery({
    queryKey: qk.quote(id, search, currency),
    queryFn: () => api.vehicles.quote(id, search, currency),
    enabled,
    placeholderData: keepPreviousData,
  });
/** Availability of the model on the dates (and hours, when given: the backend checks the exact window). */
export const useAvailability = (
  id: string,
  start: string,
  end: string,
  times?: { pickupTime: string; returnTime: string },
  enabled = true,
) =>
  useQuery({
    queryKey: qk.availability(id, start, end, times?.pickupTime, times?.returnTime),
    queryFn: () => api.vehicles.availability(id, start, end, times?.pickupTime, times?.returnTime),
    enabled,
  });
export const useRefundPolicy = (start: string, pickupTime: string) =>
  useQuery({ queryKey: qk.refund(start, pickupTime), queryFn: () => api.vehicles.refundPolicy(start, pickupTime) });

/* ── Agencies and blog ── */
export const useAgency = (slug: string) =>
  useQuery({ queryKey: qk.agency(slug), queryFn: () => api.agencies.get(slug) });
export const useBlogPosts = () => useQuery({ queryKey: qk.blog, queryFn: api.blog.list });
export const useBlogPost = (id: string) => useQuery({ queryKey: qk.blogPost(id), queryFn: () => api.blog.get(id) });

/* ── Experiences ── */
export const useExperiences = (filters: ExperienceFilters, enabled = true) =>
  useQuery({
    queryKey: qk.experiences(filters),
    queryFn: () => api.experiences.list(filters),
    placeholderData: keepPreviousData,
    enabled,
  });
export const useExperienceCategories = (city: string) =>
  useQuery({ queryKey: qk.experienceCategories(city), queryFn: () => api.experiences.categories(city) });
/** By id or public id; the record is also cached under its other id (the canonical URL costs no second GET, P6). */
export function useExperience(id: string) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: qk.experience(id),
    queryFn: async () => {
      const e = await api.experiences.get(id);
      for (const alias of [e.id, e.publicId]) if (alias && alias !== id) qc.setQueryData(qk.experience(alias), e);
      return e;
    },
  });
}
export const useExperienceDays = (id: string, from: string) =>
  useQuery({ queryKey: qk.experienceDays(id, from), queryFn: () => api.experiences.days(id, from) });
/** Server quote only: never show a client-computed price for an experience. */
export const useExperienceQuote = (sel: ExperienceSelection, enabled = true) =>
  useQuery({
    queryKey: qk.experienceQuote(sel),
    queryFn: () => api.experiences.quote(sel),
    enabled,
    placeholderData: keepPreviousData,
  });

/* ── Bookings and trips ── */
export function useCreateVehicleBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VehicleBookingInput) => api.bookings.createVehicle(input),
    onSuccess: (b) => {
      qc.setQueryData(qk.booking(b.id), b);
      void qc.invalidateQueries({ queryKey: qk.trips });
    },
  });
}
export function useCreateExperienceBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ExperienceBookingInput) => api.bookings.createExperience(input),
    onSuccess: (b) => {
      qc.setQueryData(qk.booking(b.id), b);
      void qc.invalidateQueries({ queryKey: qk.trips });
    },
  });
}
/** Confirmation polling (Decision 31): every 10 s while the agency has not confirmed and the page is open (visible tab). */
const CONFIRMATION_POLL_MS = 10_000;
export const useBooking = (id: string) =>
  useQuery({
    queryKey: qk.booking(id),
    queryFn: () => api.bookings.get(id),
    refetchInterval: (q) => (q.state.data?.status === 'pending' ? CONFIRMATION_POLL_MS : false),
    refetchIntervalInBackground: false,
  });
export const useTrips = (enabled = true) => useQuery({ queryKey: qk.trips, queryFn: api.bookings.trips, enabled });
export const useCarts = (enabled = true) => useQuery({ queryKey: qk.carts, queryFn: api.bookings.carts, enabled });
export function useCancelTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.bookings.cancel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.trips }),
  });
}
export function useRemoveCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.bookings.removeCart(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.carts }),
  });
}
export function useRateTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; stars: number }) => api.bookings.rate(v.id, v.stars),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.trips }),
  });
}

/* ── Favourites ── */
export const useFavourites = (enabled = true) =>
  useQuery({ queryKey: qk.favourites, queryFn: api.favourites.list, enabled });
export const useFavouriteIds = () => useQuery({ queryKey: qk.favouriteIds, queryFn: api.favourites.ids });
export function useToggleFavourite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.favourites.toggle(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: qk.favouriteIds });
      const prev = qc.getQueryData<string[]>(qk.favouriteIds) ?? [];
      qc.setQueryData<string[]>(qk.favouriteIds, prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
      return { prev };
    },
    onError: (_e, _id, ctx) => qc.setQueryData(qk.favouriteIds, ctx?.prev),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.favourites });
      void qc.invalidateQueries({ queryKey: qk.stats });
    },
  });
}

/* ── Profile, messages, partner ── */
export const useProfileStats = (enabled = true) =>
  useQuery({ queryKey: qk.stats, queryFn: api.profile.stats, enabled });
export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProfileUpdate) => api.profile.update(input),
    onSuccess: (user) => qc.setQueryData(qk.session, user),
  });
}
/** Conversations. Polled every 3 s only while the live connection (P8) is down: the socket updates them. */
export function useThreads(enabled = true) {
  const live = useChatLiveStatus();
  return useQuery({
    queryKey: qk.threads,
    queryFn: api.messages.threads,
    enabled,
    refetchInterval: live ? false : 3000,
  });
}
export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { threadId: string; text: string }) => api.messages.send(v.threadId, v.text),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.threads }),
  });
}
export function useMarkThreadRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.messages.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.threads }),
  });
}
export const useSubmitPartnerLead = () => useMutation({ mutationFn: (lead: PartnerLead) => api.partner.submit(lead) });
export const useNewsletterSignup = () =>
  useMutation({ mutationFn: (email: string) => api.newsletter.subscribe(email) });
/** Contact form (P14): `POST /api/send-email`. */
export const useSendContactMessage = () =>
  useMutation({ mutationFn: (message: ContactMessage) => api.contact.send(message) });

/* ── Chat (P8) and marketing consent (P11) ── */
/** The 50 messages before `beforeId` («Messages précédents»). */
export const useOlderMessages = () =>
  useMutation({ mutationFn: (v: { threadId: string; beforeId: string }) => api.chat.older(v.threadId, v.beforeId) });
/** 1–5 rating of a resolved support conversation. */
export function useRateThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { threadId: string; rating: number }) => api.chat.rate(v.threadId, v.rating),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.threads }),
  });
}
/** «Oui, je veux recevoir les offres»: the session is re-read (`marketingConsent`). */
export function useAcceptMarketing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.marketing.accept(),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.session }),
  });
}

/* ── Payments and coupons (P3, P4) ── */
/** Stripe publishable key and readiness; read once, when a card step opens. */
export const usePaymentConfig = (enabled = true) =>
  useQuery({ queryKey: qk.paymentConfig, queryFn: api.payments.config, staleTime: Infinity, enabled, retry: 1 });
/** After the card step: the server re-reads the intent and moves the reservation on. */
export function useFinalizePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paymentIntentId: string) => api.payments.finalize(paymentIntentId),
    onSettled: (r) => {
      if (r?.reservationId) void qc.invalidateQueries({ queryKey: qk.booking(r.reservationId) });
      void qc.invalidateQueries({ queryKey: qk.trips });
      void qc.invalidateQueries({ queryKey: qk.carts });
    },
  });
}
/** Re-opens the payment of an unpaid reservation (cart resume). */
export function useResumePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { reservationId: string; currency: Currency }) => api.payments.resume(v.reservationId, v.currency),
    onError: () => void qc.invalidateQueries({ queryKey: qk.carts }),
  });
}
/** One read of a reservation, without the confirmation page's polling (cart resume). */
export const useReservation = (id: string, enabled = true) =>
  useQuery({ queryKey: qk.booking(id), queryFn: () => api.bookings.get(id), enabled });
export const useCheckCoupon = () => useMutation({ mutationFn: (code: string) => api.coupons.check(code) });

/* ── Trip actions, reviews, partner status, results paging (P5, P13, P15) ── */
/** What cancelling now would refund (vehicles; read when the cancel sheet opens, never cached long). */
export const useCancelPreview = (id: string, enabled = true) =>
  useQuery({ queryKey: qk.cancelPreview(id), queryFn: () => api.trips.cancelPreview(id), enabled, staleTime: 0 });
export function useCancelTripRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; request: CancelRequest }) => api.trips.cancel(v.id, v.request),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.trips });
      void qc.invalidateQueries({ queryKey: qk.stats });
    },
  });
}
export function useRequestExtension() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; request: ExtensionRequest }) => api.trips.requestExtension(v.id, v.request),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.trips }),
  });
}
export function useRateTripCriteria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; rating: TripRatingInput }) => api.trips.rate(v.id, v.rating),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.trips });
      void qc.invalidateQueries({ queryKey: qk.reviews });
    },
  });
}
export const useVoucherPdf = () => useMutation({ mutationFn: (id: string) => api.trips.voucherPdf(id) });
/** The trip the home page asks the client to rate (once per tab, see the account feature's RatingPrompt). */
export const useTripToRate = (enabled = true) =>
  useQuery({ queryKey: qk.tripToRate, queryFn: api.trips.toRate, enabled, staleTime: Infinity, retry: false });
export const useReviews = (enabled = true) => useQuery({ queryKey: qk.reviews, queryFn: api.reviews.list, enabled });
export function useDeleteReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.reviews.remove(id),
    onSuccess: (_r, id) => {
      qc.setQueryData<Review[]>(qk.reviews, (list) => list?.filter((r) => r.id !== id));
      void qc.invalidateQueries({ queryKey: qk.reviews });
      void qc.invalidateQueries({ queryKey: qk.trips });
    },
  });
}
export const usePartnerStatus = (enabled = true) =>
  useQuery({ queryKey: qk.partnerStatus, queryFn: api.account.partnerStatus, enabled, staleTime: 5 * 60_000 });
/** Results page by page («Voir plus»): each page comes from the API. */
export const useVehicleResultsPages = (search: VehicleSearch, filters: VehicleFilters) =>
  useInfiniteQuery({
    queryKey: qk.resultsPages(search, filters),
    queryFn: ({ pageParam }) => api.results.page(search, filters, pageParam),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.totalPages ? last.page + 1 : undefined),
    placeholderData: keepPreviousData,
  });
export const useActivityCategoryName = (id: string) =>
  useQuery({
    queryKey: qk.activityCategory(id),
    queryFn: () => api.results.activityCategory(id),
    enabled: !!id,
    staleTime: Infinity,
  });

/* ── Experience booking (P6): departures, free-entry requests, provider tickets ── */
/** Open departures between two days (`/:id/slots`); refreshed every minute while shown (seats move). */
export const useExperienceSlots = (id: string, from: string, to: string, enabled = true) =>
  useQuery({
    queryKey: qk.experienceSlots(id, from, to),
    queryFn: () => api.experiences.slots(id, from, to),
    enabled: enabled && !!id && !!from && !!to,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
/** A remembered free-entry request (L10), read again on demand («Actualiser»). */
export const useEligibilityRequest = (id: string | null, enabled = true) =>
  useQuery({
    queryKey: qk.eligibility(id ?? ''),
    queryFn: () => api.experienceBooking.eligibility(id ?? ''),
    enabled: enabled && !!id,
    retry: false,
    staleTime: 0,
  });
export function useRequestEligibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EligibilityRequestInput) => api.experienceBooking.requestEligibility(input),
    onSuccess: (r) => qc.setQueryData(qk.eligibility(r.id), r),
  });
}
export const useResendTickets = () =>
  useMutation({ mutationFn: (bookingId: string) => api.experienceBooking.resendTickets(bookingId) });

/* ── Cross-sell (API-ACT-16) ── */
/**
 * Experiences for a stay: the city's experiences with an open departure between `from` and `to` (`yyyy-MM-dd`,
 * the next 14 days without them). `enabled` is false while the platform's activities switch is unknown or off.
 */
export const useCrossSellExperiences = (
  q: { city: string; from?: string; to?: string; limit?: number },
  enabled = true,
) =>
  useQuery({
    queryKey: qk.crossSellExperiences(q.city, q.from ?? '', q.to ?? '', q.limit ?? 6),
    queryFn: () => api.crossSell.experiences(q),
    enabled: enabled && !!q.city.trim(),
    staleTime: 5 * 60_000,
  });
